import { Clear, ModeEdit, SaveAs } from "@mui/icons-material";
import { Box, CircularProgress, Grid, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { emergencyContactService } from "services/emergencyContactService";
import { Select } from "components/molecules/forms/Select/Select";
import { Controller, useForm } from "react-hook-form";
import TextInput from "./forms/TextInput/TextInput";
import { notifications$ } from "services/subjects";
import { useEmergencyContact } from "hooks/useEmergencyContact";
import { finalize } from "rxjs";
import { isValidPhone } from "utils/validations";
import PhoneInput from "./forms/PhoneInput/PhoneInput";

export type FormDataState = {
  emergencyContactRelation: string | null;
  emergencyContactFullName: string | null;
  emergencyContactPhoneNumber: string | null;
  emergencyContactPhoneNumberType: string | null;
  emergencyContactEmail: string | null;
};

const DEFAULT_FORM_DATA: FormDataState = {
  emergencyContactRelation: null,
  emergencyContactFullName: null,
  emergencyContactPhoneNumber: null,
  emergencyContactPhoneNumberType: null,
  emergencyContactEmail: null,
};

export type EmergencyContactFormProps = {
  emergencyContact?: Omit<FormDataState, "emergencyContactPhoneNumberType">;
  memberId?: string;
  readOnly?: boolean;
  onUpdateSuccess?: () => void;
  initialLoading?: boolean;
};


const EmergencyContactForm: React.FC<EmergencyContactFormProps> = (props) => {
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const {
    relationships: fetchedRelationships,
    fetched,
  } = useEmergencyContact();

  const relationships = useMemo(() => [
    { value: "", description: "-- Select One --" },
    ...fetchedRelationships,
  ], [fetchedRelationships]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    formState: { errors },
  } = useForm<FormDataState>({
    defaultValues: DEFAULT_FORM_DATA,
  });

  const mapRelationship = (defaultRelationship: string): string | undefined =>
    relationships.find(
      (r) => r.value.toLowerCase() === defaultRelationship.toLowerCase()
    )?.value;

  const handleSubmitForm = (data: FormDataState) => {
    const finalForm = {
      emergencyContactPhoneNumber:
        data.emergencyContactPhoneNumber?.trim() || null,
      emergencyContactEmail: data.emergencyContactEmail?.trim() || null,
      emergencyContactFullName: data.emergencyContactFullName?.trim() || null,
      emergencyContactRelation: data.emergencyContactRelation?.trim() || null,
      emergencyContactPhoneNumberType:
        data.emergencyContactPhoneNumberType?.trim() || null,
    };

    if (finalForm.emergencyContactPhoneNumber) {
      finalForm.emergencyContactPhoneNumberType = "Phone";
    }

    finalForm.emergencyContactRelation =
      mapRelationship(finalForm.emergencyContactRelation ?? "") ??
      finalForm.emergencyContactRelation;

    setSaving(true);
    emergencyContactService
      .processEmergencyContact$(finalForm, props.memberId)
      .pipe(
        finalize(() => {
          setSaving(false);
        })
      )
      .subscribe({
        next: () => {
          notifications$.next({
            severity: "success",
            message:
              "Done! - Your request has been submitted",
          });
          setSaveEnabled(false);
            if (props.onUpdateSuccess) {
              props.onUpdateSuccess();
            }
        },
        error: () => {
          notifications$.next({
            severity: "error",
            message:
              "An error occurred while saving your emergency contact. Please try again.",
          });
        },
      });
  };

  useEffect(() => {
    if (!props.emergencyContact) {
      return;
    }
    reset({
      emergencyContactRelation: mapRelationship(props.emergencyContact.emergencyContactRelation ?? "") ??
        props.emergencyContact.emergencyContactRelation ??
        null,
      emergencyContactFullName: props.emergencyContact.emergencyContactFullName ?? null,
      emergencyContactPhoneNumber: props.emergencyContact.emergencyContactPhoneNumber ?? null,
      emergencyContactPhoneNumberType: null,
      emergencyContactEmail: props.emergencyContact.emergencyContactEmail ?? null,
    });
  }, [props.emergencyContact, relationships]);

  const initialLoading = !fetched && props.initialLoading;
  const disabled = props.readOnly || !saveEnabled || saving;

  return (
    <Box mb={4}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h4">Emergency Contact</Typography>
        {saving ? (
          <CircularProgress size={24} />
        ) : saveEnabled ? (
          <Box>
            <Button
              variant="text"
              aria-label="save"
              onClick={handleSubmit(handleSubmitForm)}
            >
              <SaveAs fontSize="small" sx={{ mr: 1 }} color="primary" />
              <Typography color="primary" fontSize="small">
                Save changes
              </Typography>
            </Button>
            <Button
              variant="text"
              aria-label="cancel"
              onClick={() => setSaveEnabled(false)}
            >
              <Clear color="error" sx={{ mr: 1 }} />
              <Typography fontSize="small" color="error">
                Clear
              </Typography>
            </Button>
          </Box>
        ) : (
          <Button
            variant="text"
            disabled={props.readOnly}
            aria-label="update emergency contact info"
            onClick={() => setSaveEnabled(true)}
          >
            <ModeEdit fontSize="small" sx={{ mr: 1 }} />
            <Typography fontSize="small">Edit</Typography>
          </Button>
        )}
      </Box>
      <Grid
        container
        spacing={2}
        columns={{
          xs: 1,
          sm: 3,
          md: 5,
        }}
      >
        <Grid size={1} component="article">
          <Controller
            control={control}
            name="emergencyContactRelation"
            rules={{
              validate: {
                requiredOnlyIfNameIsNotBlank: (value) => {
                  const name = getValues().emergencyContactFullName;
                  if (!!name?.trim() && !value) {
                    return "Relation is required";
                  }
                  return true;
                },
              },
            }}
            render={({ field }) => (
              <Select
                disabled={disabled}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                getValue={(option) => option.value}
                getKey={(option) => option.value}
                getDisplayValue={(option) => option.description}
                options={relationships}
                label="Relation to member"
                initialLoading={initialLoading}
              />
            )}
          />
          {errors.emergencyContactRelation && (
            <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>
              {errors.emergencyContactRelation.message}
            </div>
          )}
        </Grid>
        <Grid size={1} component="article">
          <TextInput
            {...register("emergencyContactFullName")}
            id="emergency_contact_name"
            label="Emergency Contact Name"
            fullWidth
            disabled={disabled}
            initialLoading={initialLoading}
            error={!!errors.emergencyContactFullName}
            helperText={errors.emergencyContactFullName?.message}
          />
        </Grid>
        <Grid size={1} component="article">
          <Controller
            control={control}
            name="emergencyContactPhoneNumber"
            rules={{
              validate: (val) =>
                isValidPhone(val ?? "") ||
                "Please provide a valid US phone number",
            }}
            render={({ field }) => (
              <PhoneInput
                id="emergency_contact_phone"
                label="Emergency Contact Phone"
                fullWidth
                disabled={disabled}
                initialLoading={initialLoading}
                error={!!errors.emergencyContactPhoneNumber}
                helperText={errors.emergencyContactPhoneNumber?.message}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            )}
          />
        </Grid>
        <Grid size={1} component="article">
          <TextInput
            {...register("emergencyContactEmail", {
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please provide a valid email address",
              },
            })}
            id="emergency_contact_email"
            label="Emergency Contact Email"
            type="email"
            fullWidth
            disabled={disabled}
            initialLoading={initialLoading}
            error={!!errors.emergencyContactEmail}
            helperText={errors.emergencyContactEmail?.message}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmergencyContactForm;
export { EmergencyContactForm };
